import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  AreaChart, Area,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Home, DollarSign, Users2,
  FileText, Wrench, AlertTriangle, Building2,
  ArrowRight, HardHat, Banknote, PieChart as PieChartIcon,
  ShieldAlert, CalendarClock, ChevronRight, Layers, ChevronDown, ChevronUp, KeyRound,
} from 'lucide-react';
import PropertyLifecycleDashboard from './PropertyLifecycleDashboard';
import ActionQueuePanel from '../components/ActionQueuePanel';
import PortfolioReviewCard from './intelligence/PortfolioReviewCard';
import { MetricInfoTooltip } from '../components/MetricInfoTooltip';
import DashboardSections from '../components/DashboardSections';
import GettingStartedCard from '../components/GettingStartedCard';
import { useIsMobile } from '../hooks/useIsMobile';
import { leasingFor } from '../lib/routes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_LABELS = {
  lead: 'Lead',
  underwriting: 'Underwriting',
  loi: 'LOI',
  under_contract: 'Under Contract',
  closing: 'Closing',
  acquired: 'Acquired',
};

const STAGE_COLORS = {
  lead: '#94a3b8',
  underwriting: '#60a5fa',
  loi: '#a78bfa',
  under_contract: '#f59e0b',
  closing: '#fb923c',
  acquired: '#22c55e',
};

function fmt(val) {
  if (val == null || val === undefined) return '$0';
  const v = Number(val);
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1000000) return `${sign}$${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(0)}K`;
  return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtFull(val) {
  if (val == null) return '$0';
  const v = Number(val);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ─── KPI Section with mobile priority + show-more expander ───
function KpiSection({ kpis }) {
  const isMobile = useIsMobile();
  const [showAll, setShowAll] = useState(false);
  const priorityKpis = kpis.filter(k => k.priority === 1);
  const secondaryKpis = kpis.filter(k => k.priority !== 1);
  const visible = isMobile && !showAll ? priorityKpis : kpis;
  const hiddenCount = kpis.length - priorityKpis.length;

  return (
    <div data-testid="kpi-section">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="kpi-grid">
        {visible.map(k => <KpiCard key={k.key} {...k.props} />)}
      </div>
      {isMobile && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(s => !s)}
          className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors min-h-[44px]"
          data-testid="kpi-show-more"
        >
          {showAll ? <><ChevronUp className="w-3.5 h-3.5" />Show fewer KPIs</> : <><ChevronDown className="w-3.5 h-3.5" />Show {hiddenCount} more KPI{hiddenCount === 1 ? '' : 's'}</>}
        </button>
      )}
    </div>
  );
}

// ─── KPI Card ───
function KpiCard({ label, value, subtitle, icon: Icon, iconBg, trend, testId, sparkData, sparkKey, sparkColor }) {
  const hasSparkline = sparkData && sparkData.length > 0 && sparkKey;
  return (
    <div
      className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 sm:p-5 hover:shadow-md transition-shadow overflow-hidden"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {trend !== undefined && trend !== null && (
          <span className={`text-xs font-semibold flex items-center gap-0.5 ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {trend >= 0 ? '+' : ''}{fmtFull(trend)}
          </span>
        )}
      </div>
      <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white leading-tight" data-testid={`${testId}-value`}>
        {value}
      </p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{label}</p>
      {subtitle && <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{subtitle}</p>}
      {hasSparkline && (
        <div className="mt-2 -mx-1 -mb-1" data-testid={`${testId}-sparkline`}>
          <ResponsiveContainer width="100%" height={40}>
            <AreaChart data={sparkData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`grad-${testId}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={sparkColor || '#3b82f6'} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={sparkColor || '#3b82f6'} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey={sparkKey}
                stroke={sparkColor || '#3b82f6'}
                strokeWidth={1.5}
                fill={`url(#grad-${testId})`}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ─── Section Wrapper ───
// RT-2 — the dashboard withholds a money section as `{ forbidden: true }`
// for a member without that section's key (dashboard:financials:view,
// dashboard:debt:view, dashboard:deals:view).
export const isWithheld = (section) => Boolean(section && section.forbidden);

function NoAccess({ what }) {
  return (
    <p className="text-sm text-slate-400 py-6 text-center" data-testid="dashboard-section-no-access">
      You don&apos;t have access to {what}.
    </p>
  );
}

function Section({ title, icon: Icon, children, action, testId }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-slate-500 dark:text-slate-400" />}
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-wide">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

/**
 * Iter 222d — "Open A/R from closing day" dashboard tile.
 * Shows every property whose closing-day cutover has produced
 * prorated rent A/R that still needs collecting, plus the per-property
 * deposit-held progress (so the operator sees the full closing-day
 * to-do list in one place).
 */
function ClosingDayARTile() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/dashboard/closing-day-ar`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ items: [], total_properties: 0, total_open_ar: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 h-32 animate-pulse" />
    );
  }
  if (!data || !data.items || data.items.length === 0) return null;

  return (
    <div
      className="bg-white dark:bg-slate-800 rounded-xl border border-indigo-200 dark:border-indigo-900/50 overflow-hidden"
      data-testid="closing-day-ar-tile"
    >
      <div className="px-5 py-3 flex items-center justify-between bg-indigo-50/60 dark:bg-indigo-950/20 border-b border-indigo-100 dark:border-indigo-900/50">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center shrink-0">
            <KeyRound className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              Open A/R from closing day
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              {data.total_properties} {data.total_properties === 1 ? 'property' : 'properties'} · ${data.total_open_ar.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pending collection
            </p>
          </div>
        </div>
      </div>
      <ul className="divide-y divide-slate-100 dark:divide-slate-700">
        {data.items.map((it) => (
          <li
            key={it.property_id}
            className="px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors cursor-pointer flex items-center justify-between gap-3"
            data-testid={`closing-day-ar-row-${it.property_id}`}
            onClick={() => navigate(leasingFor(it.property_id))}
          >
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                {it.property_name}
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                Closed {it.closing_date || '—'} · {it.active_leases} active {it.active_leases === 1 ? 'lease' : 'leases'}
              </div>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Rent A/R</div>
                <div
                  className={`text-sm font-bold ${it.open_ar_count > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}
                  data-testid={`closing-day-ar-rent-${it.property_id}`}
                >
                  {it.open_ar_count} · ${it.open_ar_total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Deposits to mark held</div>
                <div
                  className={`text-sm font-bold ${it.deposit_gap > 0.005 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}
                  data-testid={`closing-day-ar-deposits-${it.property_id}`}
                >
                  {it.deposits_held_count}/{it.active_leases} · ${it.deposit_gap.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gap
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


function DashboardPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
    // Silently process any due recurring transactions
    axios.post(`${API_URL}/api/recurring/process`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {}); // fire-and-forget
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/portfolio/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setError(null);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="dashboard-page" className="space-y-6">
        <div className="mb-2">
          <div className="h-8 w-64 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
          <div className="h-4 w-96 bg-slate-100 dark:bg-slate-800 rounded mt-2 animate-pulse" />
        </div>
        {/* KPI grid skeleton */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 h-28 animate-pulse" />
          ))}
        </div>
        {/* Two-column lower content placeholder */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 h-72 animate-pulse" />
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 h-48 animate-pulse" />
          </div>
          <div className="space-y-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 h-48 animate-pulse" />
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 h-72 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div data-testid="dashboard-page" className="text-center py-20">
        <AlertTriangle className="w-12 h-12 text-slate-400 mx-auto mb-3" />
        <p className="text-slate-500">{error || 'No data available'}</p>
        <button onClick={fetchDashboard} className="mt-4 text-blue-600 hover:underline text-sm">Retry</button>
      </div>
    );
  }

  const { deals, properties, financials, debt, maintenance, projects } = data;
  const trends = data.monthly_trends || [];
  // RT-2 — a money section the caller may not see comes back as
  // `{ forbidden: true }`, never as zeros; it renders as "no access".
  const dealsWithheld = isWithheld(deals);
  const financialsWithheld = isWithheld(financials);
  const debtWithheld = isWithheld(debt);
  // ISO-3 — without dashboard:financials:view the server sends no portfolio
  // value, rent roll, project budget or spend: hide those widgets.
  const propertyMoneyWithheld = Boolean(properties && properties.money_withheld);
  const projectMoneyWithheld = Boolean(projects && projects.money_withheld);

  // Deals pipeline chart data
  const pipelineData = dealsWithheld ? [] : Object.entries(deals.by_stage || {})
    .filter(([_, v]) => v.count > 0)
    .map(([stage, v]) => ({
      name: STAGE_LABELS[stage] || stage,
      count: v.count,
      value: v.value,
      fill: STAGE_COLORS[stage] || '#64748b',
    }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl">
          <p className="font-medium">{payload[0].payload.name}</p>
          <p>{fmtFull(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  const PipelineTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl">
          <p className="font-medium">{d.name}</p>
          <p>{d.count} deal{d.count !== 1 ? 's' : ''} &middot; {fmtFull(d.value)}</p>
        </div>
      );
    }
    return null;
  };

  // Budget utilization for projects
  const budgetPct = projects.total_budget > 0
    ? Math.round((projects.total_spent / projects.total_budget) * 100)
    : 0;

  return (
    <div data-testid="dashboard-page" className="space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="mb-1">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
          Welcome back, {user?.full_name?.split(' ')[0] || 'User'}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Portfolio overview as of {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      {/* BILL-10 — the Getting Started path, until it is done or dismissed. */}
      <GettingStartedCard />

      {/* Sprint 5C — Canonical Dashboard sibling sections (DASH-006
          through DASH-011). Single fetch to /api/dashboard/overview;
          each section gates on its own permission. */}
      <DashboardSections />

      {/* ── TOP KPI ROW ──
          Sprint 5D — DASH-013/014: YTD Cash Flow + YTD NOI removed
          from this legacy row because both are now displayed by the
          DASH-002-wrapped Sprint 5C Financials card above. Remaining
          KPIs are non-financial operational measures (Occupancy,
          Monthly Rent Roll, Properties) which Sprint 5C does not
          duplicate. They will graduate to the section contract in a
          future sprint. */}
      <KpiSection
        kpis={[
          { key: 'occupancy', priority: 1, props: {
              testId: 'kpi-occupancy', label: 'Occupancy',
              value: `${properties.occupancy_rate}%`,
              subtitle: `${properties.occupied_units} of ${properties.total_units} units`,
              icon: Users2,
              iconBg: properties.occupancy_rate >= 90 ? 'bg-emerald-600' : properties.occupancy_rate >= 70 ? 'bg-amber-500' : 'bg-red-500',
          }},
          !propertyMoneyWithheld && { key: 'rent-roll', priority: 2, props: {
              testId: 'kpi-rent-roll', label: 'Monthly Rent Roll',
              value: fmtFull(properties.monthly_rent_roll),
              subtitle: `${fmtFull(properties.annual_rent_roll)}/yr`,
              icon: Banknote, iconBg: 'bg-emerald-600',
              sparkData: trends, sparkKey: 'income', sparkColor: '#22c55e',
          }},
          { key: 'properties', priority: 2, props: {
              testId: 'kpi-properties', label: 'Properties',
              value: properties.total, subtitle: `${properties.total_units} units`,
              icon: Home, iconBg: 'bg-blue-600',
          }},
        ].filter(Boolean)}
      />

      {/* ── UNIFIED ACTION QUEUE (PROD-003/004) ──
          Replaces the old locally-computed alert strip; one queue
          shared with the Command Center and Construction module. */}
      <ActionQueuePanel limit={6} title="Needs your attention" />

      {/* ── NORTHLINE'S PORTFOLIO REVIEW (NI-25) ──
          Where to look first across the properties, and what repeats.
          Renders nothing without intelligence:view. */}
      <PortfolioReviewCard />

      {/* ── CLOSING-DAY A/R TILE (Iter 222d) ── */}
      <ClosingDayARTile />

      {/* ── PROPERTY LIFECYCLE ── */}
      <PropertyLifecycleDashboard />

      {/* ── MAIN GRID: Pipeline + Financials ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
        {/* Deals Pipeline */}
        <Section
          title="Deals Pipeline"
          icon={FileText}
          testId="deals-pipeline-section"
          action={
            <button
              onClick={() => navigate('/deals')}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
              data-testid="view-deals-link"
            >
              View All <ChevronRight className="w-3 h-3" />
            </button>
          }
        >
          {dealsWithheld ? <NoAccess what="the deals pipeline" /> : (<>
          <div className="flex items-baseline gap-3 mb-4">
            <span className="text-2xl font-bold text-slate-900 dark:text-white">{deals.total}</span>
            <span className="text-xs text-slate-500">deals &middot; {fmtFull(deals.pipeline_value)} pipeline</span>
          </div>
          {pipelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={pipelineData} barSize={32}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip content={<PipelineTooltip />} cursor={false} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {pipelineData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-400 py-8 text-center">No active deals in pipeline</p>
          )}
          {/* Stage legend */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            {Object.entries(deals.by_stage || {}).filter(([_, v]) => v.count > 0).map(([stage, v]) => (
              <div key={stage} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: STAGE_COLORS[stage] }} />
                {STAGE_LABELS[stage]}: {v.count}
              </div>
            ))}
          </div>
          </>)}
        </Section>

        {/* Sprint 5D — DASH-014: "Financial Performance (YTD)" section
            retired. Income / OpEx / NOI / Cash Flow are now shown on
            the DASH-002-wrapped Sprint 5C Financials card. Retain a
            slim "Debt service detail" tile (operational, not a
            financial-truth duplicate) so the legacy `financials.ytd_debt_service`
            value stays visible without an unguarded duplicate. */}
        <Section
          title="Debt Service (YTD)"
          icon={DollarSign}
          testId="debt-service-detail-section"
        >
          {financialsWithheld ? <NoAccess what="debt service" /> : (<>
          <div className="text-xs text-slate-500 mb-2 flex items-center gap-1">
            Cash leaving the books to lenders this year.
            <MetricInfoTooltip metricKey="debt_principal_outstanding" />
          </div>
          <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">
            {fmtFull(financials.ytd_debt_service)}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">
            Net of principal + interest postings. For trusted totals see
            the Financials card above (DASH-002).
          </p>
          </>)}
        </Section>
      </div>

      {/* ── BOTTOM GRID: Properties + Debt + Maintenance + Projects ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        {/* Portfolio Value */}
        <Section title="Portfolio" icon={Building2} testId="portfolio-section">
          <div className="space-y-3">
            {!propertyMoneyWithheld && (<>
            <div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 uppercase">Total Acquisition Cost</p>
              <p className="text-lg font-bold text-slate-900 dark:text-white">{fmtFull(properties.total_purchase_value)}</p>
            </div>
            <div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 uppercase">Current Value</p>
              <p className="text-lg font-bold text-slate-900 dark:text-white">
                {properties.portfolio_value > 0 ? fmtFull(properties.portfolio_value) : '—'}
              </p>
            </div>
            </>)}
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <Layers className="w-3.5 h-3.5" />
              {properties.total} properties &middot; {properties.total_units} units
            </div>
          </div>
        </Section>

        {/* Outstanding Debt */}
        <Section title="Debt" icon={Banknote} testId="debt-section">
          {debtWithheld ? <NoAccess what="debt" /> : (
          <div className="space-y-3">
            <div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 uppercase">Outstanding Balance</p>
              <p className="text-lg font-bold text-slate-900 dark:text-white">{fmtFull(debt.total_outstanding)}</p>
            </div>
            <div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 uppercase">Active Loans</p>
              <p className="text-lg font-bold text-slate-900 dark:text-white">{debt.active_loans}</p>
            </div>
            {/* LEG-2 — loan records with no ledger account are not in the books' debt (as on the Balance Sheet) */}
            {debt.off_ledger_loans > 0 && (
              <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="debt-off-ledger">
                {debt.off_ledger_loans} loan record{debt.off_ledger_loans === 1 ? '' : 's'} ({fmtFull(debt.off_ledger_total)})
                {' '}not on the books — not in the balance above. Link {debt.off_ledger_loans === 1 ? 'it' : 'them'} to a
                {' '}loan account under Accounting.
              </p>
            )}
            {!propertyMoneyWithheld && properties.portfolio_value > 0 && (
              <div className="text-xs text-slate-500 dark:text-slate-400">
                LTV: <span className="font-semibold text-slate-700 dark:text-slate-300">
                  {Math.round((debt.total_outstanding / properties.portfolio_value) * 100)}%
                </span>
              </div>
            )}
          </div>
          )}
        </Section>

        {/* Maintenance */}
        <Section title="Maintenance" icon={Wrench} testId="maintenance-section">
          <div className="space-y-3">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-slate-900 dark:text-white">{maintenance.open + maintenance.in_progress}</span>
              <span className="text-xs text-slate-500">active work orders</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2 text-center">
                <p className="font-bold text-slate-900 dark:text-white">{maintenance.open}</p>
                <p className="text-slate-500">Open</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2 text-center">
                <p className="font-bold text-slate-900 dark:text-white">{maintenance.in_progress}</p>
                <p className="text-slate-500">In Progress</p>
              </div>
            </div>
            {maintenance.urgent_or_high > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 font-medium">
                <AlertTriangle className="w-3 h-3" />
                {maintenance.urgent_or_high} urgent/high priority
              </div>
            )}
            {maintenance.open + maintenance.in_progress === 0 && maintenance.urgent_or_high === 0 && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">All clear — no open issues</p>
            )}
          </div>
        </Section>

        {/* Projects */}
        <Section title="Projects" icon={HardHat} testId="projects-section">
          <div className="space-y-3">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-slate-900 dark:text-white">{projects.active}</span>
              <span className="text-xs text-slate-500">active project{projects.active !== 1 ? 's' : ''}</span>
            </div>
            {!projectMoneyWithheld && (<>
            <div>
              <div className="flex justify-between text-[11px] text-slate-500 dark:text-slate-400 mb-1">
                <span>Budget Utilization</span>
                <span className="font-semibold text-slate-700 dark:text-slate-300">{budgetPct}%</span>
              </div>
              <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${budgetPct > 90 ? 'bg-red-500' : budgetPct > 70 ? 'bg-amber-500' : 'bg-blue-500'}`}
                  style={{ width: `${Math.min(budgetPct, 100)}%` }}
                />
              </div>
            </div>
            <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
              <span>Spent: <span className="font-semibold text-slate-700 dark:text-slate-300">{fmt(projects.total_spent)}</span></span>
              <span>Budget: <span className="font-semibold text-slate-700 dark:text-slate-300">{fmt(projects.total_budget)}</span></span>
            </div>
            </>)}
          </div>
        </Section>
      </div>
    </div>
  );
}

export default DashboardPage;
